"use client";

import { RoleType } from "@/00-shared/const";
import { Card, CardContent, CardHeader } from "@/03-presenters/components/ui/card";
import { formatToLocalTime, hashEmailToRandomDigit } from "@/03-presenters/shared/helper";
import { Avatar, AvatarImage } from "@/03-presenters/components/ui/avatar";
import { H4, Label } from "@/03-presenters/components/ui/typography";
import { Button } from "@/03-presenters/components/ui/button";
import type { User } from "lucia";
import Setting from "./Setting";
import Image from "next/image";
import { AccountInfo } from "@/lib/userInfo";
import { cx } from "class-variance-authority";
import UpdateUserName from "./UpdateUserName";
import UpdateUserPassword from "./UpdateUserPassword";
import BindPhone from "./BindPhone";
import { useUserInfo } from "@/app/[locale]/hooks";
import { SeaBayIcon } from "@/03-presenters/components/ui/icons";
import UpdateShareCode from "./UpdateShareCode";
import { LogOutIcon } from "lucide-react";
import { LogoutForm } from "@/03-presenters/widgets/Login/form/LogoutForm";
import { logout } from "@/03-presenters/widgets/Login/form/LogoutForm/logout.action";
function maskPhoneNumber(phoneNumber: string): string {
  try {
    return phoneNumber.replace(/(\d{3})\d{4}(\d{4})/, "$1****$2");
  } catch {
    return phoneNumber;
  }
}
export const memberMap: Record<number, {
  url: string;
  titleColor: string;
  color: string;
  title: string;
}> = {
  [RoleType.ENTERPRISE]: {
    url: "/user/enterPriseMember.png",
    titleColor: "text-[#F1EAD6]",
    color: "text-[#ECDED4]",
    title: "企业版会员"
  },
  [RoleType.PROFESSIONAL]: {
    url: "/user/professionalMember.png",
    titleColor: "text-[#D58146]",
    color: "text-[#E98335]",
    title: "专业版会员"
  },
  [RoleType.STANDARD]: {
    url: "/user/standardMember.png",
    titleColor: "text-[#4C3DEE]",
    color: "text-[#A799DB]",
    title: "标准版会员"
  },
  [RoleType.ENTERPRISE_V2]: {
    url: "/user/enterprise_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#EED3B2] bg-clip-text text-transparent",
    color: "text-[#ECDED4]",
    title: "企业版会员"
  },
  [RoleType.EXCLUSIVE]: {
    url: "/user/exclusive_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#CEC0EA] bg-clip-text text-transparent",
    color: "text-[#FAF3EBCC]",
    title: "尊享版会员"
  },
  [RoleType.PROFESSIONAL_V2]: {
    url: "/user/professional_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#FFF9ABCF] bg-clip-text text-transparent",
    color: "text-[#FAF5EBE5]",
    title: "专业版会员"
  },
  [RoleType.BASIC]: {
    url: "/user/basic_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#FFFFFFC7] bg-clip-text text-transparent",
    color: "text-[#FFFFFFD9]",
    title: "基础版会员"
  },
  [RoleType.EXPERIENCE]: {
    url: "/user/experience_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#FFFFFFC7] bg-clip-text text-transparent",
    color: "text-[#FFFFFFD9]",
    title: "体验版会员"
  },
  [RoleType.ENTERPRISE_V3]: {
    url: "/user/enterprise_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#EED3B2] bg-clip-text text-transparent",
    color: "text-[#ECDED4]",
    title: "企业版会员"
  },
  [RoleType.EXCLUSIVE_V3]: {
    url: "/user/exclusive_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#CEC0EA] bg-clip-text text-transparent",
    color: "text-[#FAF3EBCC]",
    title: "尊享版会员"
  },
  [RoleType.PROFESSIONAL_V3]: {
    url: "/user/professional_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#FFF9ABCF] bg-clip-text text-transparent",
    color: "text-[#FAF5EBE5]",
    title: "专业版会员"
  },
  [RoleType.BASIC_V3]: {
    url: "/user/basic_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#FFFFFFC7] bg-clip-text text-transparent",
    color: "text-[#FFFFFFD9]",
    title: "标准版会员"
  },
  [RoleType.EXPERIENCE_V3]: {
    url: "/user/experience_card.webp",
    titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#FFFFFFC7] bg-clip-text text-transparent",
    color: "text-[#FFFFFFD9]",
    title: "入门版会员"
  }
};
export function getMemberByRoleId(roleId: number) {
  const isEnterpriseCustom = checkIsEnterpriseCustom(roleId);
  if (isEnterpriseCustom) {
    return {
      url: "/user/custom_member.webp",
      titleColor: "bg-gradient-to-r from-[#FFFFFF] to-[#EED3B2] bg-clip-text text-transparent",
      color: "text-[#ECDED4]",
      title: "企业定制版"
    };
  }
  return memberMap[roleId];
}
export const checkIsEnterpriseCustom = (roleId: number) => {
  if (roleId > 100 && roleId < 1000) {
    return true;
  }
  return false;
};
export default function UserInfoCard({
  user,
  accountInfo,
  showLogout = false
}: {
  user: User;
  accountInfo: AccountInfo;
  showLogout?: boolean;
}) {
  const {
    seaBayCount,
    isNeverPaid
  } = useUserInfo();
  const role = accountInfo?.role_list?.[0] || {};
  const expriedTime = formatToLocalTime(role.start_at + role.last_time, "yyyy-MM-dd");
  const avatarImg = user.image ?? `/avatar/${hashEmailToRandomDigit(user?.id as string ?? "")}.png`;
  const member = getMemberByRoleId(role.id);
  return <Card data-sentry-element="Card" data-sentry-component="UserInfoCard" data-sentry-source-file="UserInfoCard.tsx">
      <CardHeader className="pb-2" data-sentry-element="CardHeader" data-sentry-source-file="UserInfoCard.tsx">
        <H4 data-sentry-element="H4" data-sentry-source-file="UserInfoCard.tsx">账户中心</H4>
      </CardHeader>
      <CardContent className="mt-6 flex flex-col gap-4" data-sentry-element="CardContent" data-sentry-source-file="UserInfoCard.tsx">
        <div className="flex items-center justify-between gap-12">
          <div className="flex items-start gap-8">
            <Avatar className="h-24 w-24" data-sentry-element="Avatar" data-sentry-source-file="UserInfoCard.tsx">
              <AvatarImage src={avatarImg} alt="avatar" data-sentry-element="AvatarImage" data-sentry-source-file="UserInfoCard.tsx" />
            </Avatar>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Label data-sentry-element="Label" data-sentry-source-file="UserInfoCard.tsx">用户ID: </Label>
                <Label data-sentry-element="Label" data-sentry-source-file="UserInfoCard.tsx">{user?.id}</Label>
              </div>
              <UpdateUserName userName={user?.username ?? ""} data-sentry-element="UpdateUserName" data-sentry-source-file="UserInfoCard.tsx" />
              <div className="flex items-center gap-2">
                <Label data-sentry-element="Label" data-sentry-source-file="UserInfoCard.tsx">出海贝: </Label>
                <Label data-sentry-element="Label" data-sentry-source-file="UserInfoCard.tsx">{seaBayCount}</Label>
                <SeaBayIcon width={18} height={17} data-sentry-element="SeaBayIcon" data-sentry-source-file="UserInfoCard.tsx" />
              </div>

              {showLogout ? <div className="mt-4">
                  <Button variant="outline" size="sm" onClick={async () => {
                await logout();
                window.location.reload();
              }}>
                    <div className="flex items-center gap-2">
                      <LogOutIcon size={16} />
                      <span>退出登录</span>
                    </div>
                  </Button>
                </div> : null}
            </div>
            {user?.phone ? <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Label>用户手机: </Label>
                  <Label>{maskPhoneNumber(user?.phone)}</Label>
                </div>
                <UpdateUserPassword />
                <UpdateShareCode shareCode={user?.shareCode} isNeverPaid={isNeverPaid} />
              </div> : <div className="flex flex-col gap-2">
                <BindPhone />
                <UpdateShareCode shareCode={user?.shareCode} isNeverPaid={isNeverPaid} />
              </div>}
          </div>
          {/* <Setting user={user} /> */}
          {member ? <div className="relative rounded-lg bg-transparent">
              <Image src={member.url} style={{
            height: 127,
            width: 340,
            objectFit: "cover"
          }} alt="card" width="340" height="127" priority />
              <span className={cx("absolute left-14 top-5 text-lg font-bold", member.titleColor, role.id > 5 && "left-7 text-xl")}>
                {member.title}
              </span>
              <span className={cx("absolute bottom-6 left-[1.6rem] text-xs", member.color)}>
                {expriedTime} 到期
              </span>
            </div> : <div className="w-[340px]"></div>}
        </div>
      </CardContent>
    </Card>;
}